import {
  Badge,
  Box,
  Button,
  Container,
  createToaster,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Input,
  Text,
  Textarea,
  VStack
} from "@chakra-ui/react";
import { useState, useEffect, useRef } from "react";
import { FiArrowLeft, FiCheck, FiChevronDown, FiCode, FiSearch, FiFileText, FiX, FiSettings, FiGlobe, FiDatabase } from "react-icons/fi";
import { RiCodeSSlashLine } from "react-icons/ri";
import { Link, useNavigate, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { mockApi } from "../api/mockApi";

// Create toaster instance
const toast = createToaster({
  placement: "top",
  pauseOnPageIdle: true
});

interface ScriptTemplate {
  id?: string;
  name: string;
  description: string;
  placement: "head" | "body";
  type: "script" | "meta" | "mixed";
  content: string;
  status: "active" | "inactive";
  isGlobal?: boolean;
  paths?: string[];
}

function CreateScript() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<ScriptTemplate>({
    name: "",
    description: "",
    placement: "head",
    type: "script",
    content: "",
    status: "inactive",
    isGlobal: false,
    paths: []
  });

  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [editorLanguage, setEditorLanguage] = useState<"html" | "xml">("html");
  const [pathDropdown, setPathDropdown] = useState({
    isOpen: false,
    searchQuery: ""
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Fetch existing script if in edit mode
  const { data: existingScript } = useQuery({
    queryKey: ["script", id],
    queryFn: () => mockApi.getScript ? mockApi.getScript(id!) : Promise.resolve(null),
    enabled: isEditMode
  });

  // Fetch available paths
  const { data: availablePaths = [] } = useQuery({
    queryKey: ["paths"],
    queryFn: mockApi.getPaths
  });

  useEffect(() => {
    if (existingScript) {
      setFormData(existingScript);
      setSelectedPaths(existingScript.paths || []);
    }
  }, [existingScript]);

  // Update editor language based on type
  useEffect(() => {
    if (formData.type === "meta") {
      setEditorLanguage("xml");
    } else {
      setEditorLanguage("html");
    }
  }, [formData.type]);

  // No need for click outside handler since we're using a modal with backdrop

  const createScriptMutation = useMutation({
    mutationFn: async (data: ScriptTemplate) => {
      if (isEditMode) {
        return mockApi.updateScript ? mockApi.updateScript(id!, data) : Promise.resolve();
      } else {
        return mockApi.createScript ? mockApi.createScript(data) : Promise.resolve();
      }
    },
    onSuccess: () => {
      toast.create({
        title: isEditMode ? "Script Updated" : "Script Created",
        description: isEditMode 
          ? "Your script template has been updated successfully."
          : "Your script template has been created successfully.",
        type: "success",
        duration: 3000
      });
      navigate("/website");
    },
    onError: (error: any) => {
      toast.create({
        title: "Error",
        description: error.message || "Failed to save script template",
        type: "error",
        duration: 5000
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const errors: Record<string, string> = {};
    
    if (!formData.name) {
      errors.name = "Template name is required";
    }
    
    if (!formData.content) {
      errors.content = "Template content is required";
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Focus on first error field
      if (errors.name && nameInputRef.current) {
        nameInputRef.current.focus();
      }
      
      toast.create({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        type: "error",
        duration: 3000
      });
      return;
    }

    createScriptMutation.mutate({
      ...formData,
      paths: formData.isGlobal ? [] : selectedPaths
    });
  };

  const handleCancel = () => {
    navigate("/website");
  };

  const getTemplateExamples = () => {
    if (formData.type === "script") {
      return `<!-- Example JavaScript -->
<script>
  // Google Analytics
  (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
  (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
  m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
  })(window,document,'script','https://www.google-analytics.com/analytics.js','ga');
  
  ga('create', 'UA-XXXXX-Y', 'auto');
  ga('send', 'pageview');
</script>`;
    } else if (formData.type === "meta") {
      return `<!-- Example Meta Tags -->
<meta name="description" content="Your page description">
<meta property="og:title" content="Your Page Title">
<meta property="og:description" content="Your page description">
<meta property="og:image" content="https://example.com/image.jpg">
<meta property="og:url" content="https://example.com/page">
<meta name="twitter:card" content="summary_large_image">`;
    } else {
      return `<!-- Mixed Content Example -->
<meta name="viewport" content="width=device-width, initial-scale=1">
<script src="https://cdn.example.com/library.js"></script>
<link rel="stylesheet" href="https://cdn.example.com/styles.css">
<script>
  console.log('Custom initialization code');
</script>`;
    }
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="7xl" py={8}>
        <VStack gap={8} align="stretch">
          {/* Header */}
          <Box>
            <Link to="/website">
              <Button
                variant="ghost"
                size="sm"
                mb={4}
                _hover={{ bg: "gray.100" }}
              >
                <Icon as={FiArrowLeft} mr={2} />
                Back to Website
              </Button>
            </Link>
            <Heading size="xl" color="gray.800">
              {isEditMode ? "Edit Script Template" : "Create New Script Template"}
            </Heading>
            <Text color="gray.600" mt={2}>
              Configure a reusable code snippet for your website paths
            </Text>
          </Box>

          <Grid templateColumns={{ base: "1fr", lg: "1fr 400px" }} gap={8}>
            {/* Main Form */}
            <VStack
              as="form"
              onSubmit={handleSubmit}
              gap={6}
              align="stretch"
              overflow="visible"
              position="relative"
            >
              {/* Template Configuration Section */}
              <Box
                bg="white"
                rounded="xl"
                shadow="sm"
                border="1px"
                borderColor="gray.200"
                overflow="visible"
                position="relative"
              >
                <Box
                  px={6}
                  py={4}
                  bg="gradient.to-r"
                  bgGradient="linear(to-r, purple.50, blue.50)"
                  borderBottom="1px"
                  borderColor="gray.200"
                >
                  <HStack gap={2}>
                    <Icon as={RiCodeSSlashLine} boxSize={5} color="purple.600" />
                    <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                      Template Configuration
                    </Text>
                  </HStack>
                </Box>

                <Box p={6} position="relative" overflow="visible">
                  <VStack gap={4} align="stretch" overflow="visible">
                    {/* Template Name */}
                    <Box>
                      <HStack mb={2} justify="space-between">
                        <Text fontSize="sm" fontWeight="medium">
                          Template Name <Text as="span" color="red.500">*</Text>
                        </Text>
                      </HStack>
                      <Input
                        ref={nameInputRef}
                        placeholder="e.g., Google Analytics Tracking"
                        value={formData.name}
                        onChange={(e) => {
                          setFormData({ ...formData, name: e.target.value });
                          if (validationErrors.name) {
                            setValidationErrors(prev => {
                              const { name, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                        size="lg"
                        borderColor={validationErrors.name ? "red.500" : undefined}
                        _focus={{
                          borderColor: validationErrors.name ? "red.500" : "blue.500",
                          boxShadow: validationErrors.name 
                            ? "0 0 0 1px var(--chakra-colors-red-500)"
                            : "0 0 0 1px var(--chakra-colors-blue-500)"
                        }}
                      />
                      {validationErrors.name && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {validationErrors.name}
                        </Text>
                      )}
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        A descriptive name for your script template
                      </Text>
                    </Box>

                    {/* Description */}
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>
                        Description
                      </Text>
                      <Textarea
                        placeholder="Describe what this script does..."
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                      />
                    </Box>

                    {/* Placement and Type */}
                    <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          Placement <Text as="span" color="red.500">*</Text>
                        </Text>
                        <HStack gap={0}>
                          <Button
                            variant={formData.placement === "head" ? "solid" : "outline"}
                            colorScheme={formData.placement === "head" ? "purple" : "gray"}
                            onClick={() => setFormData({ ...formData, placement: "head" })}
                            roundedRight="none"
                            flex={1}
                          >
                            <Icon as={FiCode} mr={2} />
                            Head
                          </Button>
                          <Button
                            variant={formData.placement === "body" ? "solid" : "outline"}
                            colorScheme={formData.placement === "body" ? "purple" : "gray"}
                            onClick={() => setFormData({ ...formData, placement: "body" })}
                            roundedLeft="none"
                            flex={1}
                          >
                            <Icon as={FiDatabase} mr={2} />
                            Body
                          </Button>
                        </HStack>
                      </Box>

                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          Content Type <Text as="span" color="red.500">*</Text>
                        </Text>
                        <HStack gap={2}>
                          {["script", "meta", "mixed"].map((type) => (
                            <Button
                              key={type}
                              size="md"
                              variant={formData.type === type ? "solid" : "outline"}
                              colorScheme={formData.type === type ? "purple" : "gray"}
                              onClick={() => setFormData({ ...formData, type: type as any })}
                              flex={1}
                              textTransform="capitalize"
                            >
                              {type}
                            </Button>
                          ))}
                        </HStack>
                      </Box>
                    </Grid>
                  </VStack>
                </Box>
              </Box>

              {/* Code Editor Section */}
              <Box
                bg="white"
                rounded="xl"
                shadow="sm"
                border="1px"
                borderColor="gray.200"
                overflow="visible"
              >
                <Box
                  px={6}
                  py={4}
                  bg="gradient.to-r"
                  bgGradient="linear(to-r, blue.50, purple.50)"
                  borderBottom="1px"
                  borderColor="gray.200"
                >
                  <HStack justify="space-between">
                    <HStack gap={2}>
                      <Icon as={FiCode} boxSize={5} color="blue.600" />
                      <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                        Template Content
                      </Text>
                    </HStack>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFormData({ ...formData, content: getTemplateExamples() })}
                    >
                      <Icon as={FiFileText} mr={2} />
                      Load Example
                    </Button>
                  </HStack>
                </Box>

                <Box p={6}>
                  <Box
                    border="1px"
                    borderColor={validationErrors.content ? "red.500" : "gray.200"}
                    rounded="lg"
                    overflow="hidden"
                    height="400px"
                  >
                    <Editor
                      height="100%"
                      defaultLanguage={editorLanguage}
                      language={editorLanguage}
                      value={formData.content}
                      onChange={(value) => {
                        setFormData({ ...formData, content: value || "" });
                        if (validationErrors.content) {
                          setValidationErrors(prev => {
                            const { content, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                      theme="vs-light"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        wordWrap: "on",
                        automaticLayout: true,
                        formatOnPaste: true,
                        formatOnType: true,
                        padding: { top: 16, bottom: 16 }
                      }}
                    />
                  </Box>
                  {validationErrors.content && (
                    <Text color="red.500" fontSize="sm" mt={2}>
                      {validationErrors.content}
                    </Text>
                  )}
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Use placeholders like {`{{variable}}`} for dynamic content
                  </Text>
                </Box>
              </Box>

              {/* Path Selection Section */}
              <Box
                bg="white"
                rounded="xl"
                shadow="sm"
                border="1px"
                borderColor="gray.200"
                overflow="visible"
              >
                <Box
                  px={6}
                  py={4}
                  bg="gradient.to-r"
                  bgGradient="linear(to-r, green.50, blue.50)"
                  borderBottom="1px"
                  borderColor="gray.200"
                >
                  <HStack gap={2}>
                    <Icon as={FiGlobe} boxSize={5} color="green.600" />
                    <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                      Target Paths
                    </Text>
                  </HStack>
                </Box>

                <Box p={6}>
                  <VStack align="stretch" gap={4}>
                    {/* Global Script Toggle */}
                    <Box
                      as="button"
                      p={4}
                      bg={formData.isGlobal ? "blue.50" : "gray.50"}
                      rounded="lg"
                      border="1px"
                      borderColor={formData.isGlobal ? "blue.200" : "gray.200"}
                      cursor="pointer"
                      transition="all 0.2s"
                      _hover={{ borderColor: formData.isGlobal ? "blue.300" : "gray.300" }}
                      onClick={() => {
                        setFormData({ ...formData, isGlobal: !formData.isGlobal });
                        if (!formData.isGlobal) {
                          setSelectedPaths([]);
                        }
                      }}
                      w="full"
                    >
                      <HStack justify="space-between">
                        <VStack align="start" gap={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            Apply to All Pages
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            Enable this to apply the script globally across all paths
                          </Text>
                        </VStack>
                        <Box
                          position="relative"
                          w="50px"
                          h="26px"
                          bg={formData.isGlobal ? "blue.500" : "gray.300"}
                          rounded="full"
                          transition="all 0.2s"
                        >
                          <Box
                            position="absolute"
                            top="2px"
                            left={formData.isGlobal ? "26px" : "2px"}
                            w="22px"
                            h="22px"
                            bg="white"
                            rounded="full"
                            shadow="sm"
                            transition="all 0.2s"
                          />
                        </Box>
                      </HStack>
                    </Box>

                    {!formData.isGlobal && (
                      <>
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" mb={2}>
                            Select Specific Paths
                          </Text>
                          <Box position="relative">
                        <Box
                          as="button"
                          w="full"
                          p={3}
                          border="1px"
                          borderColor="gray.300"
                          rounded="lg"
                          bg="white"
                          textAlign="left"
                          onClick={() => {
                            setPathDropdown(prev => ({
                              isOpen: !prev.isOpen,
                              searchQuery: ""
                            }));
                          }}
                          _hover={{ borderColor: "gray.400" }}
                          _focus={{
                            borderColor: "purple.500",
                            boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                            outline: "none"
                          }}
                        >
                          <HStack justify="space-between">
                            <Text>
                              {selectedPaths.length === 0 
                                ? "Select paths..."
                                : selectedPaths.length === availablePaths.length
                                ? "All paths selected"
                                : `${selectedPaths.length} path${selectedPaths.length !== 1 ? "s" : ""} selected`
                              }
                            </Text>
                            <Icon 
                              as={FiChevronDown} 
                              transform={pathDropdown.isOpen ? "rotate(180deg)" : "none"}
                              transition="transform 0.2s"
                            />
                          </HStack>
                        </Box>

                        {/* Dropdown Modal */}
                        {pathDropdown.isOpen && (
                          <>
                            {/* Backdrop */}
                            <Box
                              position="fixed"
                              top={0}
                              left={0}
                              right={0}
                              bottom={0}
                              bg="blackAlpha.400"
                              zIndex={1400}
                              onClick={() => setPathDropdown({ isOpen: false, searchQuery: "" })}
                            />
                            
                            {/* Modal Content */}
                            <Box
                              position="fixed"
                              bg="white"
                              border="1px"
                              borderColor="gray.200"
                              rounded="lg"
                              shadow="2xl"
                              zIndex={1500}
                              w="600px"
                              maxW="90vw"
                              display="flex"
                              flexDirection="column"
                              left="50%"
                              top="50%"
                              transform="translate(-50%, -50%)"
                              maxHeight="80vh"
                            >
                              {/* Header with Search */}
                              <Box p={4} borderBottom="1px solid" borderColor="gray.200">
                                <HStack justify="space-between" mb={3}>
                                  <Text fontWeight="semibold" fontSize="lg">Select Target Paths</Text>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setPathDropdown({ isOpen: false, searchQuery: "" })}
                                    aria-label="Close"
                                  >
                                    <Icon as={FiX} />
                                  </Button>
                                </HStack>
                                <HStack>
                                  <Icon as={FiSearch} color="gray.400" />
                                  <Input
                                    placeholder="Search paths..."
                                    value={pathDropdown.searchQuery}
                                    onChange={(e) =>
                                      setPathDropdown(prev => ({
                                        ...prev,
                                        searchQuery: e.target.value
                                      }))
                                    }
                                    size="md"
                                    border="1px"
                                    borderColor="gray.300"
                                    _focus={{
                                      borderColor: "purple.500",
                                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </HStack>
                              </Box>

                              {/* Select/Clear All Actions */}
                              {!pathDropdown.searchQuery && (
                                <Box p={3} borderBottom="1px solid" borderColor="gray.200">
                                  <HStack justify="space-between">
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="blue"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPaths(
                                          availablePaths.map((p: any) => p.id)
                                        );
                                      }}
                                    >
                                      Select All ({availablePaths.length})
                                    </Button>
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="gray"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedPaths([]);
                                      }}
                                    >
                                      Clear All
                                    </Button>
                                  </HStack>
                                </Box>
                              )}

                              {/* Path List - Scrollable */}
                              <Box flex="1" overflowY="auto" maxH="300px">
                              {(() => {
                                const filteredPaths = availablePaths.filter((path: any) => {
                                  const searchQuery = pathDropdown.searchQuery.toLowerCase();
                                  return (
                                    path.path.toLowerCase().includes(searchQuery) ||
                                    (path.description && path.description.toLowerCase().includes(searchQuery))
                                  );
                                });

                                return filteredPaths.length > 0 ? (
                                  filteredPaths.map((path: any) => {
                                    const isSelected = selectedPaths.includes(path.id);
                                    return (
                                      <Box
                                        key={path.id}
                                        as="button"
                                        w="full"
                                        p={3}
                                        textAlign="left"
                                        _hover={{ bg: "gray.50" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (isSelected) {
                                            setSelectedPaths(prev => prev.filter(p => p !== path.id));
                                          } else {
                                            setSelectedPaths(prev => [...prev, path.id]);
                                          }
                                        }}
                                        borderBottom="1px solid"
                                        borderColor="gray.100"
                                      >
                                        <HStack justify="space-between">
                                          <HStack gap={3}>
                                            <Box
                                              w={4}
                                              h={4}
                                              border="2px"
                                              borderColor={isSelected ? "purple.500" : "gray.300"}
                                              rounded="sm"
                                              bg={isSelected ? "purple.500" : "white"}
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="center"
                                            >
                                              {isSelected && (
                                                <Icon as={FiCheck} boxSize={2.5} color="white" />
                                              )}
                                            </Box>
                                            <VStack align="start" gap={0}>
                                              <Text fontSize="sm" fontWeight="medium">
                                                {path.path}
                                              </Text>
                                              {path.description && (
                                                <Text fontSize="xs" color="gray.500">
                                                  {path.description}
                                                </Text>
                                              )}
                                            </VStack>
                                          </HStack>
                                          {path.status && (
                                            <Badge
                                              colorScheme={path.status === "active" ? "green" : "gray"}
                                              variant="subtle"
                                              size="sm"
                                            >
                                              {path.status}
                                            </Badge>
                                          )}
                                        </HStack>
                                      </Box>
                                    );
                                  })
                                ) : (
                                  <Box p={4} textAlign="center">
                                    <Text fontSize="sm" color="gray.500">
                                      No paths found matching "{pathDropdown.searchQuery}"
                                    </Text>
                                  </Box>
                                );
                              })()}
                              </Box>

                              {/* Footer with Done button */}
                              <Box p={3} borderTop="1px solid" borderColor="gray.200" bg="gray.50">
                                <HStack justify="space-between">
                                  <Text fontSize="sm" color="gray.600">
                                    {selectedPaths.length} path{selectedPaths.length !== 1 ? "s" : ""} selected
                                  </Text>
                                  <Button
                                    size="sm"
                                    colorScheme="purple"
                                    onClick={() => setPathDropdown({ isOpen: false, searchQuery: "" })}
                                  >
                                    Done
                                  </Button>
                                </HStack>
                              </Box>
                            </Box>
                          </>
                        )}
                      </Box>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Select which paths should include this script template
                      </Text>
                    </Box>
                      </>
                    )}
                    
                    {formData.isGlobal && (
                      <Box
                        p={4}
                        bg="gray.50"
                        rounded="lg"
                        textAlign="center"
                      >
                        <Icon as={FiGlobe} boxSize={8} color="blue.500" mb={2} />
                        <Text fontSize="sm" fontWeight="medium" color="gray.700">
                          Global Script Enabled
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          This script will be applied to all paths automatically
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Flex justify="flex-end" gap={3}>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  size="lg"
                  px={8}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="purple"
                  size="lg"
                  px={8}
                  loading={createScriptMutation.isPending}
                  disabled={!formData.name || !formData.content}
                >
                  {isEditMode ? "Update Template" : "Create Template"}
                </Button>
              </Flex>
            </VStack>

            {/* Right Side - Summary Panel */}
            <VStack gap={6} align="stretch">
              {/* Summary Panel */}
              <Box
                bg="white"
                shadow="sm"
                rounded="lg"
                p={6}
                position="sticky"
                top={8}
              >
                <HStack mb={4}>
                  <Icon as={FiSettings} color="purple.500" boxSize={5} />
                  <Text fontWeight="semibold" fontSize="lg">
                    Configuration Summary
                  </Text>
                </HStack>

                <VStack align="stretch" gap={3}>
                  {/* Template Name */}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Template Name
                    </Text>
                    {formData.name ? (
                      <Text fontSize="md" fontWeight="medium">
                        {formData.name}
                      </Text>
                    ) : (
                      <Text fontSize="md" color="gray.500">
                        Not configured
                      </Text>
                    )}
                  </Box>

                  {/* Placement & Type */}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                      Script Settings
                    </Text>
                    <Box p={2} bg="purple.50" rounded="md" borderLeft="3px solid" borderColor="purple.400">
                      <VStack align="start" gap={1}>
                        <HStack gap={2}>
                          <Text fontSize="sm" fontWeight="medium">
                            Placement:
                          </Text>
                          <Badge colorScheme={formData.placement === "head" ? "orange" : "blue"}>
                            {formData.placement.toUpperCase()}
                          </Badge>
                        </HStack>
                        
                        <HStack gap={2}>
                          <Text fontSize="sm" fontWeight="medium">
                            Type:
                          </Text>
                          <Badge colorScheme={
                            formData.type === "script" ? "blue" :
                            formData.type === "meta" ? "purple" : "green"
                          }>
                            {formData.type.toUpperCase()}
                          </Badge>
                        </HStack>
                      </VStack>
                    </Box>
                  </Box>

                  {/* Status */}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                      Status
                    </Text>
                    <HStack>
                      <Button
                        size="sm"
                        variant={formData.status === "active" ? "solid" : "outline"}
                        colorScheme="green"
                        onClick={() => setFormData({ ...formData, status: "active" })}
                      >
                        Active
                      </Button>
                      <Button
                        size="sm"
                        variant={formData.status === "inactive" ? "solid" : "outline"}
                        colorScheme="gray"
                        onClick={() => setFormData({ ...formData, status: "inactive" })}
                      >
                        Inactive
                      </Button>
                    </HStack>
                  </Box>

                  {/* Target Paths */}
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                      Target Paths
                    </Text>
                    {formData.isGlobal ? (
                      <Box p={2} bg="blue.50" rounded="md">
                        <HStack gap={2}>
                          <Icon as={FiGlobe} boxSize={3} color="blue.500" />
                          <Text fontSize="sm" color="blue.700">
                            Applied to all paths (Global)
                          </Text>
                        </HStack>
                      </Box>
                    ) : selectedPaths.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">
                        No paths selected
                      </Text>
                    ) : (
                      <VStack align="start" gap={1}>
                        {availablePaths
                          .filter((path: any) => selectedPaths.includes(path.id))
                          .slice(0, 5)
                          .map((path: any) => (
                            <HStack key={path.id} gap={2}>
                              <Icon as={FiCheck} boxSize={3} color="green.500" />
                              <Text fontSize="sm">
                                {path.path}
                              </Text>
                            </HStack>
                          ))}
                        {selectedPaths.length > 5 && (
                          <Text fontSize="sm" color="gray.500">
                            +{selectedPaths.length - 5} more
                          </Text>
                        )}
                      </VStack>
                    )}
                  </Box>

                  {/* Content Preview */}
                  {formData.content && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                        Content Preview
                      </Text>
                      <Box
                        p={3}
                        bg="gray.50"
                        rounded="md"
                        fontSize="xs"
                        fontFamily="mono"
                        color="gray.700"
                        maxH="150px"
                        overflowY="auto"
                      >
                        <Text whiteSpace="pre-wrap">
                          {formData.content.substring(0, 300)}
                          {formData.content.length > 300 && "..."}
                        </Text>
                      </Box>
                    </Box>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Grid>
        </VStack>
      </Container>
    </Box>
  );
}

export default CreateScript;